import {Action, ChecklistObject, CurrencyType, DateRangeType, FileUploadOptions, KeyTextString, Preset} from "./types";
import {FormGenFreetextType} from "./FormGenAutocomplete";
import {ReactElement} from "react";
import {PathProps} from "./index";
import {FormGenRichContentEditorBase} from "./FormGenRichContentEditorBase";
import {PasskeyConfig} from "./passkey";

export const FORMGEN_STYLE_FLAG_NO_CONTAINER = 0x1 << 1;

export type FormGenSetValueResult = {
  errorMessage?: string,
}

export enum FormGenMetadataType {
  NONE = "none",
  BOOLEAN = "boolean",
  STRING = "string",
  TEXT = "text",
  RICH_TEXT = "rich_text",
  RICH_CONTENT = "rich_content",
  CURRENCY = "currency",
  COPYABLE = "copyable",
  EMAIL = "email",
  NUMBER = "number",
  ENUM = "enum",
  DATE = "date",
  TIME = "time",
  DATETIME = "datetime",
  DATENUM = "datenum",
  TIMENUM = "timenum",
  DATETIMENUM = "datetimenum",
  DATERANGE = "daterange",
  OBJECT = "object",
  CUSTOM = "custom",
  PROFILE_PHOTO = "profile_photo",
  ICON = "icon",
  COLOR = "color",
  AUTOCOMPLETE = "autocomplete",
  COUNTRY_SELECT = "country_select",
  FILE = "file",
  ARRAY_STRING = "array_string",
  ARRAY_AUTOCOMPLETE = "array_autocomplete",
  ARRAY_FILE = "array_file",
  PASSKEY = "passkey",
  CHECKLIST = "checklist",
}

export interface FormGenMetadata {
  id?: string,
  index?: number,
  // "none" type is not rendered, but its metadata can be used to hideOn or tab-group other items.
  // Note that "object" type is for nested objects, "custom" is when you want to supply your own renderer.
  // "currency" type is an integer as "basis points", i.e. float number upto 2 decimal places multiplied by 100. Rendered by dividing value by 100.
  type: FormGenMetadataType | string;
  required?: boolean,
  hidden?: boolean,
  autoFocus?: boolean,
  styleFlags?: number,
  maxLength?: number,
  multilineStringRows?: number,
  size?: "xsmall" | "small" | "medium" | "large";
  ref?: string;
  name: string;
  readonly?: boolean;
  readonlyFn?: (target: any) => boolean;
  noneditable?: boolean;
  separator?: "above" | "below";
  sectionTitle?: string;
  description?: string;
  placeholder?: string;
  // visibility?: string; // Is this used?
  tooltip?: string;
  hideOn?: {
    propertyId: string, // id of property on which to filter.
    hide: (target: any, value: any) => boolean,
  },
  multiStep?: boolean,

  // String only
  slug?: boolean,
  slugLabel?: string,

  // String'ish only
  transformText?: ((str: string) => string) | "uppercase" | "lowercase",

  // Currency only
  currencyType?: CurrencyType,

  // Daterange only
  dateRangeDefaultType?: DateRangeType; // Used only when the daterange value is not set.

  // File only
  fileMimeTypes?: string[],
  fileRecord?: boolean, // Allows inline recording if supported by the mimetype (e.g. images, audio & video)
  fileUploadOptions?: FileUploadOptions,
  fileStoredDataSource?: string,
  filePreviewSize?: "small" | "medium" | "large";
  filePreviewType?: "image" | "audio" | "video";
  filePreviewAudioElement?: HTMLAudioElement,
  fileActions?: (args: any[]) => Action[],

  // Enum only
  enumDefaultKey?: string,
  enumVariant?: "select" | "button" | "radio",
  enumFilter?: (value: KeyTextString) => boolean,
  enumValues?: KeyTextString[],
  enumDescriptions?: KeyTextString[],
  enumMulti?: boolean,
  enumPresetsFilter?: (value: Preset) => boolean,
  enumPresetsKeyType?: string, // Name of the presets list (see PresetsKey)
  enumPresetsKeyPropertyName?: string, // defaults to 'key'
  enumPresetsTextPropertyName?: string,// defaults to 'text'

  // Autocomplete only
  autocompleteShowDescriptionInInput?: boolean, // Show description as part of the items in the drop down.
  autocompleteShowDescriptionInMenu?: boolean, // Show description as part of the items in the drop down.
  autocompleteResetOnChange?: boolean,
  autocompleteMulti?: boolean,
  autocompleteFreetextType?: FormGenFreetextType,
  autocompleteLoader?: (value?: string, inputText?: string) => Promise<KeyTextString[]>,

  // Rich content only
  richContentType?: typeof FormGenRichContentEditorBase,

  // Custom only
  customRenderer?: (target: any, setValue: (value: any) => void, path?: PathProps) => ReactElement,

  // Multistep only
  multiStepIndex?: number,

  // Passkey only
  passkeyConfig?: PasskeyConfig,

  // Checklist only
  checklistMulti?: boolean,
  checklistCreate?: boolean,
  checklistEdit?: boolean,
  checklistName?: string,
  checklistCreator?: (name?: string) => ChecklistObject,
  checklistLoader?: (value?: string[], name?: string) => Promise<ChecklistObject>,

  onSetValue?: (target: any, value: any) => FormGenSetValueResult | null | undefined | any, // Before value is set.
  onSetTransformValue?: (target: any, value: any) => any,
  onValueSet?: (target: any, value: any, updateCallback: () => Promise<void>) => void, // After value is set.

  _asyncInit?: (metadata: FormGenMetadata) => Promise<void>,
  _propertyKey?: string,
}

export const formGenMetadataKey = (propertyKey: string) => "__formgen_metadata_" + propertyKey;

export function FormGen(metadata: FormGenMetadata) {
  return function (target: any, propertyKey: string) {
    metadata._propertyKey = propertyKey;
    target[formGenMetadataKey(propertyKey)] = metadata;
    if (metadata.enumValues) {
      target[propertyKey + "__enumValues"] = metadata.enumValues;
    }
  };
}

export const enumValue = <T>(obj: T, propName: keyof T) => {
  const array = obj[propName.toString() + "__enumValues"];
  const key = obj[propName];
  return array.find(item => item.key === key)?.text;
}

export abstract class FormGenType<T> {

  protected constructor(readonly model?: T) {
  }

  abstract toModel(): T;
}
